import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { CLASSROOMS_KEY } from '@teetsh/app/src/queries/classrooms/useClassrooms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { Tag } from '@teetsh/app/src/types/event';
import _omit from 'lodash-es/omit';
import { TagWithClassrooms } from '@teetsh/app/src/containers/MesAnneesScolaires/School/Tags/types';
import { TAGS_KEY } from '@teetsh/app/src/queries/tags/useTags';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import { useTranslation } from 'react-i18next';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';

function updateTag({
  schoolyearId,
  schoolId,
  tag,
}: {
  schoolyearId: string;
  schoolId: string;
  tag: Tag;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/tags/${tag.id}`;
  return request(requestURL, {
    method: 'PATCH',
    body: _omit(tag, 'classrooms'),
  });
}

interface Props {
  schoolyearId: string;
  schoolId: string;
}

export default function useUpdateTags(props: Props) {
  const { schoolyearId, schoolId } = props;
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['schoolyearSettings']);
  const { mutateAsync } = useMutation({
    mutationFn: (tag: TagWithClassrooms) =>
      updateTag({ schoolId, schoolyearId, tag }),
    onMutate: tag => {
      queryClient.setQueryData<TagWithClassrooms[]>(
        [schoolyearId, schoolId, TAGS_KEY],
        data => (data || []).map(tt => (tt.id === tag.id ? tag : tt)),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLASSROOMS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [schoolyearId, schoolId, EDT_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [schoolyearId, schoolId, EVENTS_KEY],
      });
    },
    onError: () => {
      notifyError({
        title: t('schoolyearSettings:messages.tag.updateError'),
      });
      queryClient.invalidateQueries({
        queryKey: [schoolyearId, schoolId, TAGS_KEY],
      });
    },
  });
  return mutateAsync;
}
